<script>
  import { onMount, createEventDispatcher } from "svelte";
  import { Children } from "../../store.js";
  import { SupportUser, Response, isFormAlreadySubmitted } from "./store.js";
  import wrappedFetch from "../../wrappedFetch.js";

  import Field from "../../components/FormField.svelte";
  import Select from "../../components/HTMLElements/Select.svelte";
  import TextBox from "../../components/HTMLElements/Textbox.svelte";
  import Spinner from "../../components/HTMLElements/Spinner.svelte";
  import DOB from "../../components/HTMLElements/DOB.svelte";
  import Modal from "../../components/HTMLElements/ModalMessage.svelte";
  import Steps from "./steps.svelte";

  import * as objectWrapper from "../../components/Utils/ObjectWrapper.js";
  import * as validate from "../../components/Utils/ValidateFormFIelds.js";

  let children = [];
  let selectedChild = "";
  let errMsg = "";
  let reasonText = "";
  let isPageLoading = true;
  let showChildNameTextBox = false;
  let reference = "";
  let showModal = false;
  let isDirty = false;

  let childObject = { forename: "", surname: "", dob: "" };
  let dobStr;
  let wrappedObj;

  const browsers = [
    { label: "Google Chrome" },
    { label: "Microsoft Edge" },
    { label: "Mozilla Firefox" },
    { label: "Safari" },
    { label: "Opera" },
    { label: "Brave" },
    { label: "Internet Explorer 11 or Older" },
    { label: "Samsung Internet" },
    { label: "Other" }
  ];

  let selectedBrowser = "";

  const operatingSystems = [
    { label: "Windows 11" },
    { label: "Windows 10" },
    { label: "Windows 8 / 8.1" },
    { label: "Windows 7 or Older" },
    { label: "macOS (Sonoma, Ventura, Monterey, Big Sur)" },
    { label: "Linux (Ubuntu, Fedora, etc.)" },
    { label: "Chrome OS (Chromebook)" },
    { label: "iOS (iPhone/iPad)" },
    { label: "Android" },
    { label: "Other" }
  ];

  let selectedOS = "";

  // Fetch children from API
  const getChildren = async () => {
    const resp = await wrappedFetch("/child/getchildren", { method: "post" });
    const data = await resp.json();
    return data.children;
  };

  const dispatch = createEventDispatcher();

  // Update reason text when user types
  const textAreaValue = (e) => {
    reasonText = e.target.value;
  };

  // Set a short-lived cookie to prevent double submits
  const addFormSubmittedCookie = () => {
    const expires = new Date(Date.now() + 120 * 1000).toUTCString();
    document.cookie = `cookieName=formJustSubmitted; expires=${expires};path=/;`;
  };

  // Handle form submission
  const handleSubmit = async () => {
    isDirty = true;

    if (showChildNameTextBox) {
      if (!validate.validate(wrappedObj)) {
        errMsg = "Please check required fields have valid data.";
        wrappedObj = wrappedObj;
        return;
      } else {
        selectedChild = `${wrappedObj.forename.value} ${wrappedObj.surname.value} (${dobStr})`;
      }
    }

	const validOS = operatingSystems.some(os => os.label === selectedOS);
	const validBrowser = browsers.some(browser => browser.label === selectedBrowser);

    if (selectedChild === "" || reasonText === "" || !validOS || !validBrowser) {
      errMsg = "Please ensure that you have completed the required fields.";
      return;
    }


    $Response.childname = selectedChild;
    $Response.reason = reasonText;

    if (showChildNameTextBox) {
      $Response.childObject = wrappedObj;
    }

    $Response.step4 = true;
    $Response.OS = selectedOS;
    $Response.Browser = selectedBrowser;
    $Response.username = $SupportUser ? $SupportUser.username : "";

    const response = await wrappedFetch(`/support/SaveContactFormData`, {
      method: "post",
      body: JSON.stringify($Response),
      headers: {
        "Content-Type": "application/json"
      }
    });

    reference = await response.text();

    if (reference) addFormSubmittedCookie();

    showModal = true;
  };

  // Navigate to summary view after form submission
  const viewForm = () => {
    showModal = false;
    dispatch("pageChange", "/support/summary");
  };

  // Close modal and navigate back depending on user state
  const handleModalClose = () => {
    showModal = false;
    if ($SupportUser) {
      dispatch("pageChange", "/user/home");
    } else {
      dispatch("pageChange", "/");
    }
  };

  // On component mount, initialize state
  onMount(async () => {
    if (!$Response.step3 || isFormAlreadySubmitted()) {
      dispatch("pageChange", "/support/step3");
    }

    showChildNameTextBox = $SupportUser == null;

    if ($SupportUser != null && $Children.length < 1) {
      Children.set(await getChildren());
    }

    children = $Children;

    children.forEach((child, index) => {
      children[index].label = `${child.forename} ${child.surname} (${child.dobStr})`;
    });

    wrappedObj = objectWrapper.wrapObject(childObject);

    if ($Response.step4) {
      selectedChild = $Response.childname;
      reasonText = $Response.reason;

      if ($Response.childObject) {
        wrappedObj = $Response.childObject;
      }
    }

    isPageLoading = false;
  });
</script>

{#if showModal}
  <Modal
    bind:showModal
    buttonText="Exit"
    on:continue={handleModalClose}>
    <h1 class="title is-3">Thank you</h1>
    <p>Thank you for completing the form. We will get back to you shortly.</p>
    <p>
      If you do need to speak to us at any point, the unique reference for your query is
      <b>{reference}</b>.
    </p>
    <button class="button is-small" on:click={viewForm}>
      View Submitted Form
    </button>
  </Modal>
{/if}

<Steps
  backUrl="/support/step3"
  buttonText="Submit"
  disabled={reasonText.length < 1}
  bind:isPageLoading
  on:handleNext={handleSubmit}
  on:pageChange={(e) => dispatch("pageChange", e.detail)}>
  
  <div class="column">
    {#if !showChildNameTextBox}
      <Field label="Select child this query relates to">
        <span slot="inputField">
          <Select
            items={children}
            bind:selectedValue={selectedChild}
            isRequired={true}
            {isDirty}
            selectedValueText="label"
            label="child name"
            placeholder="--Please select a child--" />
        </span>
      </Field>
      <a href="#" on:click|preventDefault={() => { selectedChild = ""; showChildNameTextBox = true; }}>
        Child not listed above?
      </a>
    {:else}
      <div class="columns is-desktop">
        <div class="column">
          <Field label="Child first name">
            <span slot="inputField">
              <TextBox
                placeholder="Child first name"
                isRequired={true}
                bind:text={wrappedObj.forename.value}
                bind:isValid={wrappedObj.forename.isValid}
                bind:isDirty={wrappedObj.forename.isDirty}
                label="child first name" />
            </span>
          </Field>
        </div>

        <div class="column">
          <Field label="Child surname">
            <span slot="inputField">
              <TextBox
                placeholder="Child surname"
                isRequired={true}
                bind:text={wrappedObj.surname.value}
                bind:isValid={wrappedObj.surname.isValid}
                bind:isDirty={wrappedObj.surname.isDirty}
                label="child surname" />
            </span>
          </Field>
        </div>

        <div class="column">
          <Field label="Date of birth" hasIconRight={false}>
            <span slot="inputField">
              <DOB
                bind:dobStr={dobStr}
                bind:dob={wrappedObj.dob.value}
                bind:isValid={wrappedObj.dob.isValid}
                bind:isDirty={wrappedObj.dob.isDirty} />
            </span>
          </Field>
        </div>
      </div>

      {#if $SupportUser != null}
        <a href="#" on:click|preventDefault={() => (showChildNameTextBox = false)}>
          Go back to children list
        </a>
      {/if}
    {/if}

    <br />

    <Field label="Please provide details of the problem you are experiencing:">
      <span slot="inputField">
        <textarea
          maxlength="1000"
          value={reasonText}
          on:input={textAreaValue}
          rows="7"
          style="width:100%;margin-top:10px;padding:5px;" />
        <p style="font-size:0.8rem;">
          ({reasonText.length} of 1000 characters used)
        </p>
      </span>
    </Field>

    <br />

    <p>
      Please complete the following section regarding your operating system and browser to help us accurately diagnose and resolve the issue you're experiencing, as some problems may be specific to certain platforms or browser versions.
    </p>

    <div class="columns is-desktop">
      <div class="column">
        <Field label="Select Operating system">
          <span slot="inputField">
            <Select
              items={operatingSystems}
              bind:selectedValue={selectedOS}
              isRequired={true}
              {isDirty}
              selectedValueText="label"
              label="Operating System"
              placeholder="--Please select an operating system--" />
          </span>
        </Field>
      </div>

      <div class="column">
        <Field label="Select Browser">
          <span slot="inputField">
            <Select
              items={browsers}
              bind:selectedValue={selectedBrowser}
              isRequired={true}
              {isDirty}
              selectedValueText="label"
              label="Browser"
              placeholder="--Please select a browser--" />
          </span>
        </Field>
      </div>
    </div>

    <div>
      {#if errMsg !== ''}      
        <div class="notification is-invalid">
          <p class="help" style="margin-bottom:0px">{@html errMsg}</p>
        </div>
      {/if}
    </div>
  </div>
</Steps>
